import type {
  AnalyticsDataProps,
  GroupedLinesAnalyticsData,
} from '@/components/GroupedLines/components/GroupedLinesContent/GroupedLinesContent.types';
import { getCompetitorsLineName } from '@/utils';

export const getAnalyticsData = ({
  isOpen,
  isFavorite,
  championshipLines,
  isFavoritePage,
  isInSearch,
  isOutrightLine,
  activeLinesCount,
}: AnalyticsDataProps): GroupedLinesAnalyticsData => {
  const line = championshipLines[0];

  const sportCategory = line?.sport?.title;
  const categoryTitle = line?.category?.title;
  const text = 'competitors' in line ? getCompetitorsLineName(line) : categoryTitle;
  const championshipTitle = line?.championship?.title;
  const matchId = line?.championshipId;

  let dropdownAnalyticsEventName = '';

  const dropdownAnalyticsData: Record<string, string | number | undefined> = {
    cls: 'sportLines_linesGroup_dropdown',
  };

  if (isOutrightLine) {
    dropdownAnalyticsEventName = isOpen
      ? 'sportOutright_championshipName_dropdown_close'
      : 'sportOutright_championshipName_dropdown_open';

    dropdownAnalyticsData.sport_category = sportCategory;
    dropdownAnalyticsData.category = categoryTitle;
    dropdownAnalyticsData.championships = championshipTitle;
  } else {
    dropdownAnalyticsEventName = isOpen
      ? 'sportLines_linesGroup_dropdown_close'
      : 'sportLines_linesGroup_dropdown_open';

    dropdownAnalyticsData.count = activeLinesCount;
  }

  const analyticsData = {
    dropdown: {
      click: {
        eventName: dropdownAnalyticsEventName,
        data: dropdownAnalyticsData,
      },
    },
    sportLink: {
      click: {
        eventName: 'sportLines_sportCategory_click',
        data: {
          cls: 'sportLines_sportCategory',
        },
      },
    },
    categoryLink: {
      click: {
        eventName: 'sportLines_categoryName_click',
        data: {
          cls: 'sportLines_categoryName',
          sport_category: sportCategory,
        },
      },
    },
    championShipLink: {
      click: {
        eventName: isInSearch
          ? 'sport_searchResult_championshipsEvents_name_click'
          : 'sportLines_championshipName_click',
        data: {
          cls: 'sportLines_championshipName',
          sport_category: sportCategory,
        },
      },
    },
    matchName: {
      click: {
        eventName: 'sportLines_matchName_click',
        data: {
          cls: 'sportLines_matchName',
          sport_category: sportCategory,
        },
      },
    },
    matchNameInSearch: {
      click: {
        eventName: 'sport_searchResult_championshipsEvents_matchName_click',
        data: {
          cls: 'sportLines_matchName',
          sport_category: sportCategory,
        },
      },
    },
    favoritesInSearch: {
      click: {
        eventName: isFavorite
          ? 'sport_searchResult_championshipsEvents_delFavorite_icon_click'
          : 'sport_searchResult_championshipsEvents_addFavorite_icon_click',
        data: {
          cls: 'sportLines_lineGroup_favorite',
          sport_category: sportCategory,
          text,
          category: categoryTitle,
          championships: championshipTitle,
        },
      },
    },
    favorites: {
      click: {
        eventName: isFavorite
          ? 'sportLines_lineGroup_delFavorite_icon_click'
          : 'sportLines_lineGroup_addFavorite_icon_click',
        data: {
          cls: 'sportLines_lineGroup_favorite',
          sport_category: sportCategory,
          text,
          category: categoryTitle,
          championships: championshipTitle,
        },
      },
    },
    sportLinesOddSelect: {
      click: {
        eventName: isFavorite ? 'sportLines_odd_unselect' : 'sportLines_odd_select',
        data: {
          cls: 'sportLines_odd',
          sport_category: sportCategory,
          category: categoryTitle,
          championships: championshipTitle,
          match_id: championshipLines[0].championshipId,
        },
      },
    },
    sportLinesOddSelectInSearch: {
      click: {
        eventName: 'sport_searchResult_championshipsEvents_match_odd_select',
        data: {
          cls: 'sportLines_odd',
          sport_category: sportCategory,
          category: categoryTitle,
          championships: championshipTitle,
          match_id: championshipLines[0].championshipId,
        },
      },
    },
    sportLinesOddUnSelectInSearch: {
      click: {
        eventName: 'sport_searchResult_championshipsEvents_match_odd_unselect',
        data: {
          cls: 'sportLines_odd',
          sport_category: sportCategory,
          category: categoryTitle,
          championships: championshipTitle,
          match_id: championshipLines[0].championshipId,
        },
      },
    },
    sportLinesOddUnSelect: {
      click: {
        eventName: 'sportLines_odd_unselect',
        data: {
          cls: 'sportLines_odd',
          sport_category: sportCategory,
          category: categoryTitle,
          championships: championshipTitle,
          match_id: championshipLines[0].championshipId,
        },
      },
    },
    widgets: {
      click: {
        eventName: 'sportLines_matchList_broadcast_icon_click',
        data: {
          text,
          cls: 'sportLines_matchList_broadcast_icon',
          sport_category: sportCategory,
          category: categoryTitle,
          championships: championshipTitle,
          match_id: championshipLines[0].championshipId,
        },
      },
    },
    addFavoriteLine: {
      click: {
        eventName: isInSearch
          ? 'sport_searchResult_championshipsEvents_match_addFavorite_icon_click'
          : 'sportLines_outcomes_addFavorite_icon_click',
        data: {
          text,
          cls: 'sportLines_outcomes',
          sport_category: sportCategory,
          category: categoryTitle,
          championships: championshipTitle,
          match_id: championshipLines[0].championshipId,
        },
      },
    },
    removeFavoriteLine: {
      click: {
        eventName: isInSearch
          ? 'sport_searchResult_championshipsEvents_match_delFavorite_icon_click'
          : 'sportLines_outcomes_delFavorite_icon_click',
        data: {
          text,
          cls: 'sportLines_outcomes',
          sport_category: sportCategory,
          category: categoryTitle,
          championships: championshipTitle,
          match_id: championshipLines[0].championshipId,
        },
      },
    },
  };

  const analyticsDataFavorite = {
    sportLinesOddSelect: {
      click: {
        eventName: 'sportFavorite_odd_select',
        data: {
          sport_category: sportCategory,
          category: categoryTitle,
          championships: championshipTitle,
          match_id: matchId,
        },
      },
    },
    sportLinesOddUnSelect: {
      click: {
        eventName: 'sportLines_odd_unselect',
        data: {
          sport_category: sportCategory,
          category: categoryTitle,
          championships: championshipTitle,
          match_id: matchId,
        },
      },
    },
    addFavoriteLine: {
      click: {
        eventName: 'sportFavorite_line_addFavorite_icon_click',
        data: {
          sport_category: sportCategory,
          category: categoryTitle,
          championships: championshipTitle,
        },
      },
    },
    removeFavoriteLine: {
      click: {
        eventName: 'sportFavorite_line_delFavorite_icon_click',
        data: {
          cls: 'sportFavorite_line_delfavorite_icon_click',
          sport_category: sportCategory,
          category: categoryTitle,
          championships: championshipTitle,
        },
      },
    },

    favorites: {
      click: {
        eventName: isFavorite
          ? 'sportFavorite_lineGroup_delFavorite_icon_click'
          : 'sportFavorite_lineGroup_addFavorite_icon_click',
        data: {
          sport_category: sportCategory,
          category: categoryTitle,
          championships: championshipTitle,
        },
      },
    },
    dropdown: {
      click: {
        eventName: isOpen ? 'sportFavorite_lineGroup_dropdown_close' : 'sportFavorite_lineGroup_dropdown_open',
        data: {
          sport_category: sportCategory,
          category: categoryTitle,
          championships: championshipTitle,
        },
      },
    },
    categoryLink: {
      click: {
        eventName: 'sportFavorite_lineGroup_name_click',
        data: {
          sport_category: sportCategory,
          category: categoryTitle,
        },
      },
    },
    sportLink: {
      click: {
        eventName: 'sportFavorite_lineGroup_icon_click',
        data: {
          sport_category: sportCategory,
          category: categoryTitle,
        },
      },
    },
    matchName: {
      click: {
        eventName: 'sportFavorite_line_click',
        data: {
          sport_category: sportCategory,
          category: categoryTitle,
        },
      },
    },
  };

  return isFavoritePage ? analyticsDataFavorite : analyticsData;
};
